/**
 * Slovak.
 *
 * Not a Czech dictionary with three letters changed. The kitchen and the bar in
 * a Prague venue are frequently Slovak, and so is the manager; a half-translated
 * screen reads worse than an honestly foreign one.
 */

import type { Messages } from "./en";

export const sk: { locale: string; messages: Messages } = {
  locale: "sk",
  messages: {
    // --- Sign-in -----------------------------------------------------------

    // --- Role names ---------------------------------------------------------
    // The roster showed raw identifiers — "manager", "staff" — which is the same
    // database key on a screen that made the kitchen header read "KITCHEN".
    "role.owner": "Majiteľ",
    "role.admin": "Správca",
    "role.manager": "Manažér",
    "role.shift_lead": "Vedúci zmeny",
    "role.staff": "Obsluha",
    "role.viewer": "Náhľad",
    "signIn.title": "Prihlásenie",
    "signIn.subtitle": "Zadajte svoj PIN — rovnaký, aký používate na pokladni.",
    "signIn.staff": "Kto ste",
    "signIn.staffPlaceholder": "napr. Eva S.",
    "signIn.staffHint": "Meno, ako ho ukazuje pokladňa.",
    "signIn.pin": "PIN",
    "signIn.submit": "Prihlásiť sa",
    "signIn.working": "Prihlasujem…",
    "signIn.badCredentials": "Nesprávne meno alebo PIN.",
    "signIn.notBackOffice": "Táto rola nemá prístup do kancelárie.",
    "signIn.unreachable": "Server neodpovedá. Skontrolujte pripojenie.",
    "signIn.tooMany": "Príliš veľa pokusov. Skúste to znova o {minutes} min.",
    "signIn.signedInAs": "Prihlásený(á):",
    "signIn.signOut": "Odhlásiť sa",
    "signIn.expiresAt": "Relácia vyprší",
    // --- Command palette ---------------------------------------------------
    "palette.label": "Prejsť na obrazovku",
    "palette.placeholder": "Hľadať obrazovku…",
    "palette.open": "Hľadať",
    "palette.here": "ste tu",
    "palette.nothing": "Nič také tu nie je",
    "palette.hintMove": "pohyb",
    "palette.hintOpen": "otvoriť",
    "palette.hintClose": "zavrieť",
    "shell.menu": "Menu",
    "shell.closeMenu": "Zavrieť menu",

    "app.title": "Kancelária",
    "nav.today": "Dnes",
    "nav.sales": "Tržby",
    "nav.checks": "Účtenky",
    "nav.shifts": "Zmeny",
    "nav.kitchen": "Kuchyňa",
    "nav.fiscal": "Dane",
    "nav.tableCards": "Karty na stoly",
    "tableCards.lede": "Vytlačte jednu kartu na stôl. Hosť ju naskenuje, otvorí si účet a objedná — nikto nemusí prísť.",
    "tableCards.tablesLabel": "Stoly",
    "tableCards.placeholder": "12\n13\nterasa 3",
    "tableCards.hint": "Jeden stôl na riadok, tak ako ich voláte. Počet kariet:",
    "tableCards.duplicates": "Dvakrát je tu:",
    "tableCards.print": "Vytlačiť karty",
    "tableCards.expires": "Karty prestanú platiť",
    "tableCards.callToAction": "Naskenujte a objednajte",
    "tableCards.modeWarning": "Táto prevádzka otvára účty na pokladni. Karty sa vytlačia, ale kým model neprepnete v Nastaveniach, hosť uvidí „spýtajte sa obsluhy“.",
    "tableCards.noBaseUrl": "Nie je nastavené, kde beží aplikácia pre hostí, takže karta nesie len kód. Fotoaparát ho ponúkne skopírovať namiesto otvoriť.",
    "nav.menu": "Menu",
    "nav.staff": "Ľudia",
    "nav.settings": "Nastavenia",
    "nav.group.running": "Prevádzka",
    "nav.group.money": "Peniaze",
    "nav.group.setup": "Nastavenia",
    "nav.group.soon": "Pripravujeme",

    "common.loading": "Načítavam…",
    "common.failed": "Nepodarilo sa načítať",
    "common.retry": "Skúsiť znova",
    "common.empty": "Zatiaľ nič",
    "common.today": "Dnes",
    "common.week": "Posledných 7 dní",
    "common.month": "Posledných 30 dní",
    "common.custom": "Vlastné",
    "common.from": "Od",
    "common.to": "Do",
    "common.unreachable": "Server neodpovedal. Toto nie sú nuly — hodnoty nie sú známe.",
    "common.of": "z",
    "common.total": "Spolu",
    "common.date": "Dátum",
    "common.time": "Čas",
    "common.none": "Žiadne",

    "today.takings": "Dnešná tržba",
    "today.checks": "Účtenky",
    "today.average": "Priemerný účet",
    "today.open": "Otvorené stoly",
    "today.openMoney": "Na otvorených stoloch",
    "today.vat": "DPH",
    "today.payments": "Spôsoby platby",
    "today.quiet": "Dnes sa zatiaľ nič nepredalo.",
    "today.quietHint": "Obchodný deň začína o 4:00.",

    "sales.heading": "Tržby",
    "sales.day": "Obchodný deň",
    "sales.checkCount": "Účteniek",
    "sales.revenue": "Tržba",
    "sales.breakdown": "Rozpis tržieb",
    "sales.tab.items": "Položky",
    "sales.tab.staff": "Obsluha",
    "sales.tab.hours": "Hodiny",
    "sales.category": "Kategória",
    "sales.item": "Položka",
    "sales.quantity": "Množstvo",
    "sales.discounts": "Zľavy",
    "sales.server": "Obsluha",
    "sales.voids": "Storná",
    "sales.hour": "Hodina",
    "sales.other": "Ostatné",
    "sales.vat": "DPH",
    "sales.net": "Bez DPH",
    "sales.covers": "Hostí",
    "sales.perCover": "Na hosťa",
    "sales.coversHint": "{n} hostí pri stoloch — predaj pri pulte sa nepočíta",
    "sales.average": "Priemer",
    "sales.tips": "Prepitné",
    "sales.rounding": "Zaokrúhlenie",
    "sales.byRate": "Podľa sadzby DPH",
    "sales.byTender": "Podľa spôsobu platby",
    "sales.gaps": "Dni bez tržby",
    "sales.gapsWhy":
      "Chýbajúci obchodný deň bráni uzavretiu obdobia — DPH na výstupe sa viaže na dátum dodania, takže neskoro podaný deň znamená dodatočné priznanie.",
    "sales.tipsNotRevenue": "Prepitné je záväzok voči personálu, nie je to tržba.",

    "checks.heading": "Účtenky",
    "checks.number": "Číslo",
    "checks.table": "Stôl",
    "checks.opened": "Otvorené",
    "checks.closed": "Uzavreté",
    "checks.state": "Stav",
    "checks.stateOpen": "Otvorený",
    "checks.openSince": "Otvorený od {day}",
    "checks.stateSettled": "Uzavretý",
    "checks.outstanding": "Zostáva uhradiť",
    "checks.staff": "Obsluhoval",
    "checks.lines": "Položky",
    "checks.voided": "Stornované",
    "voidReason.RUNG_IN_ERROR": "Zle naúčtované",
    "voidReason.GUEST_CHANGED_MIND": "Hosť si to rozmyslel",
    "voidReason.SENT_BACK": "Vrátené z kuchyne",
    "voidReason.SPILLED": "Rozliate alebo spadnuté",
    "voidReason.OTHER": "Iný dôvod",
    "checks.tenders": "Platby",
    "checks.anomalies": "Čo nesedelo",
    "checks.noNumber": "Bez čísla účtenky",
    "checks.tab": "Účet {n}",
    "checks.fiscal.reported": "Nahlásené finančnej správe",
    "checks.fiscal.test": "Potvrdené TESTOVACOU službou",
    "checks.fiscal.testWhy": "Testovacie potvrdenie nič nehlási. Táto tržba nie je evidovaná.",
    "checks.fiscal.refused": "Odmietnuté",
    "checks.fiscal.pending": "Zatiaľ nenahlásené",
    "checks.fiscal.none": "Pre túto tržbu nebolo nikdy zaradené podanie",

    "shifts.heading": "Zmeny",
    "shifts.cashier": "Pokladník",
    "shifts.expected": "Očakávané",
    "shifts.counted": "Napočítané",
    "shifts.variance": "Rozdiel",
    "shifts.open": "Stále otvorená",
    "shifts.uncounted": "Zatiaľ nespočítané",
    "shifts.over": "Prebytok",
    "shifts.short": "Manko",
    "shifts.exact": "Presne",
    "shifts.ok": "V tolerancii",
    "shifts.watch": "Stojí za pozretie",
    "shifts.alert": "Treba prešetriť",
    "shifts.breakdown": "Z čoho očakávaná suma vychádza",
    "shifts.breakdownShort": "Rozpis",
    "shifts.openingFloatMinor": "Počiatočná hotovosť",
    "shifts.cashSalesMinor": "Tržba v hotovosti",
    "shifts.cashRefundsMinor": "Vrátené hotovosťou",
    "shifts.roundingMinor": "Zaokrúhlenie",
    "shifts.payInsMinor": "Vklady",
    "shifts.payOutsMinor": "Výdavky",
    "shifts.safeDropsMinor": "Odvody do trezora",
    "shifts.cashTipsMinor": "Prepitné v hotovosti",
    "shifts.tipOutsMinor": "Vyplatené prepitné",
    "shifts.movements": "Pohyby hotovosti",
    "shifts.overIsNotSafe":
      "Opakovaný prebytok hlásime rovnako hlasno ako manko: býva to hotovosť vybratá z pokladne a zatiaľ neodnesená.",
    "shifts.unattributed":
      "Hotovosť zaúčtovaná bez otvorenej pokladne. Nie je v žiadnom Z-reporte nižšie a nemá sa s čím porovnať.",
    "shifts.ambiguous": "z toho s dvoma otvorenými pokladňami naraz",

    "kitchen.heading": "Kuchyňa",
    "kitchen.median": "Medián času na výdaj",
    "kitchen.p90": "Najpomalšia desatina",
    "kitchen.tickets": "Tikety",
    "kitchen.served": "Vydané",
    "kitchen.station": "Stanica",
    "kitchen.late": "Neskoré tikety",
    "kitchen.whyPercentile": "Percentily, nie priemer — hosť zažíva ten koniec, nie stred.",

    "fiscal.heading": "Finančná správa",
    "fiscal.eet": "EET",
    "fiscal.notSetUp": "Nenastavené",
    "fiscal.notSetUpWhy": "Povinné od 1. 1. 2027. DIČ poplatníka a číslo prevádzky sa zadávajú v Nastaveniach.",
    "fiscal.credentials": "Podpisový certifikát",
    "fiscal.present": "Nahraný",
    "fiscal.absent": "Chýba",
    "fiscal.absentWhy": "Bez podpisového certifikátu sa žiadna tržba neodošle.",
    "fiscal.oldestPending": "Najstaršia doteraz nenahlásená tržba",
    "fiscal.quarantined": "Trvale odmietnuté",
    "fiscal.attempts": "pokusov",
    "fiscal.andMore": "a ďalšie, ktoré tu nie sú:",
    "fiscal.showRows": "Zobraziť ďalšie účtenky",
    "fiscal.kind.NO_TILL_ID": "Pokladnica nemá fiškálne ID",
    "fiscal.kind.NO_RECEIPT_NUMBER": "Účet nemá číslo účtenky",
    "fiscal.kind.NUMBER_OUTSIDE_LEASE": "Číslo účtenky je mimo pridelený rozsah",
    "fiscal.kind.NUMBER_ALREADY_ASSIGNED": "Číslo účtenky už patrí inému účtu",
    "fiscal.playground": "Potvrdené testovacou službou",
    "fiscal.playgroundWhy":
      "Testovacie potvrdenie nič nevybavuje. Tieto tržby vyzerajú ako nahlásené a nahlásené nie sú.",
    "fiscal.unreportable": "Nedá sa nahlásiť",
    "fiscal.clean": "Nič nečaká a nič nebolo odmietnuté.",
    "fiscal.numbering": "Číslovanie účteniek",
    "fiscal.gaps": "Medzery",
    "fiscal.gapsClean": "Žiadne medzery — každé pridelené číslo bolo použité alebo vrátené.",
    "fiscal.issued": "Vydané",

    "menu.heading": "Menu",

    "menu.tab.items": "Položky",

    "menu.tab.categories": "Kategórie",

    "menu.tab.groups": "Prílohy a úpravy",

    "menu.tab.stations": "Miesta prípravy",

    "menu.search": "Hľadať v menu",

    "menu.allCategories": "Všetko",

    "menu.uncategorised": "Bez kategórie",

    "menu.category": "Kategória",

    "menu.newItem": "Nová položka",

    "menu.newCategory": "Nová kategória",

    "menu.newGroup": "Nová skupina",

    "menu.newStation": "Nové miesto prípravy",

    "menu.editItem": "Upraviť",

    "menu.off": "Vypnuté",

    "menu.switchOff": "Vypnúť",

    "menu.switchOn": "Zapnúť",

    "menu.empty": "Zatiaľ tu nič nie je.",

    "menu.saved": "Uložené. Na pokladne sa dostane po publikovaní.",

    "menu.saveFailed": "Nepodarilo sa uložiť: {detail}",

    "menu.priceHint": "Vrátane DPH, ako ju uvidí hosť.",

    "menu.rateAfterPublish": "po publikovaní",

    "menu.itemCount": "Položiek: {count}",

    "menu.usedBy": "Pri položkách: {count}",

    "menu.moveUp": "Vyššie",

    "menu.moveDown": "Nižšie",

    "menu.remove": "Odobrať",

    "menu.addOption": "Pridať možnosť",

    "menu.required": "Povinné",

    "menu.optional": "Voliteľné",

    "menu.noGroups": "Prílohy a úpravy — napríklad „Príloha“ alebo „Bez cibule“ — pokladňa ponúkne pri markovaní. Povinnú otvorí sama.",

    "menu.taxCategory": "Daňová kategória",

    "menu.tax.FOOD": "Jedlo",

    "menu.tax.NON_ALC_BEVERAGE": "Nealkoholický nápoj",

    "menu.tax.ALCOHOL": "Alkohol",

    "menu.tax.TAP_WATER": "Voda z vodovodu",

    "menu.tax.MILK_BEVERAGE": "Mliečny nápoj",

    "menu.tax.NON_FOOD": "Nepotravinový tovar",
    "menuImport.open": "Importovať z tabuľky",
    "menuImport.title": "Import menu z tabuľky",
    "menuImport.lead": "Súbor CSV — z Excelu cez „Uložiť ako CSV“, alebo export zo Storyous. Položky sa pridajú do konceptu; pokladne ich nepredávajú, kým menu nezverejníte.",
    "menuImport.file": "Súbor",
    "menuImport.choose": "Vybrať súbor…",
    "menuImport.noFile": "Nie je vybraný žiadny súbor",
    "menuImport.unreadable": "Súbor sa nepodarilo prečítať ako tabuľku.",
    "menuImport.header": "Prvý riadok sú nadpisy",
    "menuImport.column.name": "Názov",
    "menuImport.column.price": "Cena s DPH",
    "menuImport.column.category": "Kategória",
    "menuImport.column.vat": "Sadzba DPH",
    "menuImport.column.none": "—",
    "menuImport.columnN": "Stĺpec {n}",
    "menuImport.items": "Položiek",
    "menuImport.station": "Stanica",
    "menuImport.tax": "Daň",
    "menuImport.uncategorised": "Bez kategórie",
    "menuImport.problems": "Tieto riadky sa nepridajú",
    "menuImport.problem.name": "chýba názov",
    "menuImport.problem.price": "cena sa nedá prečítať",
    "menuImport.taxHint": "Daň sa riadi tým, čo položka je. Sadzbu zo súboru len porovnáme so zákonom: pivo aj Kofola majú pri stole 21 %, alkohol je však len pivo.",
    "menuImport.mismatches": "Sadzba v súbore nesedí so zákonom — položky sa pridajú, skontrolujte ich",
    "menuImport.mismatch": "v súbore {file} %, pri stole {rate} %",
    "menuImport.line": "riadok {line}",
    "menuImport.more": "a ďalších {count}",
    "menuImport.add": "Pridať do konceptu ({count})",
    "menuImport.adding": "Pridávam…",
    "menuImport.done": "Pridané do konceptu: {created}.",
    "menuImport.skipped": "Už v menu bolo, preskočené: {count}.",
    "menuImport.publishHint": "Skontrolujte ich a zverejnite menu — dovtedy ich pokladne nepredávajú.",
    "menuImport.close": "Hotovo",
    "setup.heading": "Skôr než začnete predávať",
    "setup.progress": "Hotovo {done} z {total}",
    "setup.hide": "Skryť",
    "setup.open": "Otvoriť",
    "setup.step.company": "Firma na účtenke — názov a IČO",
    "setup.step.menu": "Menu, zverejnené na pokladne",
    "setup.step.tables": "Stoly",
    "setup.step.people": "Ľudia a ich PINy",
    "setup.step.publish": "Prevádzka zverejnená na pokladne",
    "setup.step.devices": "Spárované pokladne a kuchynské obrazovky",
    "setup.step.pohoda": "Pokladne v Pohode (pre účtovníka)",
    "menuImport.failed": "Import sa nepodaril: {detail}",

    "menu.field.name": "Názov",

    "menu.field.price": "Cena pre hosťa",

    "menu.field.description": "Popis",

    "menu.field.ageRestricted": "Len od 18 rokov",

    "menu.field.active": "V ponuke",

    "menu.field.groups": "Prílohy a úpravy",

    "menu.field.min": "Musí vybrať aspoň",

    "menu.field.max": "Môže vybrať najviac",

    "menu.field.maxUnlimited": "bez obmedzenia",

    "menu.field.options": "Možnosti",

    "menu.field.optionName": "Možnosť",

    "menu.field.priceDelta": "Príplatok",

    "menu.field.maxQuantity": "Najviac ks",

    "menu.problem.name": "Skupina potrebuje názov.",

    "menu.problem.optionName": "Každá možnosť potrebuje názov.",

    "menu.problem.price": "Príplatok musí byť suma, napríklad 25 alebo 25,50.",

    "menu.problem.tooFew": "Vyžaduje viac volieb, než ponúka — položku s ňou by nešlo namarkovať.",

    "menu.problem.max": "Maximum musí byť celé číslo, aspoň rovné minimu.",

    "allergen.GLUTEN": "Lepok",

    "allergen.CRUSTACEANS": "Kôrovce",

    "allergen.EGGS": "Vajcia",

    "allergen.FISH": "Ryby",

    "allergen.PEANUTS": "Arašidy",

    "allergen.SOYBEANS": "Sója",

    "allergen.MILK": "Mlieko",

    "allergen.NUTS": "Orechy",

    "allergen.CELERY": "Zeler",

    "allergen.MUSTARD": "Horčica",

    "allergen.SESAME": "Sezam",

    "allergen.SULPHITES": "Siričitany",

    "allergen.LUPIN": "Vlčí bôb",

    "allergen.MOLLUSCS": "Mäkkýše",
    "menu.edit": "Upraviť cenu",
    "menu.editing": "Cena, ktorú vidí hosť, vrátane DPH.",
    "menu.save": "Uložiť",
    "menu.cancel": "Späť",
    "menu.available": "V predaji",
    "menu.soldOut": "Došlo",
  "menu.availability": "Dostupnosť",
  "menu.availabilityFailed": "Nepodarilo sa načítať, čo je práve vypredané. Stĺpec Dostupnosť nemusí byť aktuálny.",
  "menu.markSoldOut": "Označiť ako došlé",
  "menu.soldOutReason": "Dôvod",
  "menu.soldOutDefault": "Došlo",
  "menu.soldOutConfirm": "Stiahnuť z ponuky",
  "menu.backOn": "Vrátiť do ponuky",
    "menu.draft": "Zmeny sa prejavia až po publikovaní.",
    "menu.publish": "Publikovať novú verziu",
    "menu.publishing": "Publikujem…",
    "menu.published": "Publikované ako verzia {version}. Pokladne si ju načítajú do minúty.",
    "menu.editFailed": "Zmenu sa nepodarilo uložiť.",
    "menu.version": "Verzia",
    "menu.item": "Položka",
    "menu.station": "Pripravuje",
    "menu.dineIn": "Na mieste",
    "menu.takeaway": "So sebou",
    "menu.rate": "DPH",
    "menu.allergens": "Alergény",
    "menu.whyTwoPrices":
      "To isté jedlo má na mieste 12 % a so sebou 21 %. Sadzba sa odvodzuje pri každej položke, neukladá sa k produktu.",

    "staff.heading": "Ľudia",
    "staff.name": "Meno",
    "staff.role": "Rola",
    "staff.add": "Pridať človeka",
    "staff.nameHint": "Tak, ako ho uvidí pokladňa — „Jana N.“",
    "staff.pin": "PIN",
    "staff.newPin": "Nový PIN",
    "staff.pinHint": "Šesť až dvanásť číslic. Povedzte mu ho — už sa nikde neukáže.",
    "staff.pinResetHint": "Nechajte prázdne, ak PIN nemeníte.",
    "staff.active": "Pracuje u nás",
    "staff.added": "Pridané. Na pokladni sa objaví pri ďalšej synchronizácii.",
    "staff.saved": "Uložené.",
    "staff.savedTills": "Uložené. Pokladne zabudnú starý PIN aj rolu pri ďalšej synchronizácii.",
    "staff.left": "Už u nás nepracujú",
    "staff.count": "{n} v tíme",
    "venue.version": "Na pokladniach verzia",
    "venue.unpublished": "Zmeny ešte nie sú na pokladniach",
    "venue.publish": "Zverejniť na pokladne",
    "venue.saved": "Uložené. Na pokladne sa dostane po zverejnení.",
    "venue.published": "Zverejnené — pokladne si ho stiahnu hneď.",
    "venue.company": "Firma na doklade",
    "venue.guests": "Hostia",
    "venue.guestOrdering": "Hostia môžu objednávať z telefónu",
    "venue.guestOrderingLead": "QR kód na účte vždy ukáže útratu. Objednávať z neho ide, len keď je toto zapnuté — platí hneď, bez zverejnenia.",
    "venue.companyLead": "Tak, ako to musí byť na daňovom doklade.",
    "venue.legalName": "Obchodné meno",
    "venue.registrationId": "IČO",
    "venue.vatId": "DIČ (len platiteľ DPH)",
    "venue.address": "Sídlo",
    "venue.eic": "EET: DIČ poplatníka",
    "venue.unit": "EET: číslo prevádzky",
    "venue.tables": "Stoly",
    "venue.addTable": "Pridať stôl",
    "venue.noArea": "Bez oblasti",
    "venue.seats": "miest",
    "venue.seatsLabel": "Počet miest",
    "venue.newTable": "Nový stôl",
    "venue.editTable": "Upraviť stôl",
    "venue.ownerOnly": "Upraviť môže len majiteľ.",
    "venue.seats.one": "miesto",
    "venue.seats.few": "miesta",
    "venue.seats.many": "miesta",
    "venue.seats.other": "miest",
    "venue.tableName": "Názov",
    "venue.area": "Oblasť",
    "venue.tableActive": "Používa sa",
    "devices.heading": "Zariadenia",
    "devices.pair": "Spárovať zariadenie",
    "devices.codeFor": "Kód pre",
    "devices.codeHow": "Na novom zariadení otvorte aplikáciu a zadajte ho.",
    "devices.codeUntil": "Platí raz, do",
    "devices.done": "Hotovo",
    "devices.name": "Názov",
    "devices.namePlaceholder": "napr. Tablet pri bare",
    "devices.kind": "Druh",
    "devices.till": "Pokladňa",
    "devices.kitchen": "Kuchynská obrazovka",
    "devices.station": "Stanovište",
    "devices.makeCode": "Vytvoriť kód",
    "devices.unpair": "Odpárovať",
    "devices.place": "Kde stojí",
    "devices.atTables": "Pri stoloch",
    "devices.atCounter": "Pri pulte — zaplatiť hneď, číslo objednávky",
    "devices.pickupScreen": "Výdaj — obrazovka s číslami pre hostí",
    "devices.counterChip": "Pult {prefix}",
    "devices.toCounter": "K pultu",
    "devices.toTables": "Ku stolom",
    "venue.noTables": "Zatiaľ tu nie sú žiadne stoly. Pridajte prvý — pokladnice ich uvidia po zverejnení.",
    "devices.none": "Zatiaľ tu nie je žiadne zariadenie. Prvú pokladnicu spárujete kódom.",

    // --- The accountant's file (Pohoda) -----------------------------------
    "tender.CASH": "V hotovosti",
    "tender.CARD": "Kartou",
    "tender.MEAL_VOUCHER": "Stravné lístky",
    "tender.GIFT_CARD": "Darčeková karta",
    "tender.HOUSE_ACCOUNT": "Na účet podniku",
    "tender.QR_TRANSFER": "QR platba",
    "tender.VOUCHER": "Poukaz",
    "tender.ROOM_CHARGE": "Na izbu",
    "tender.EXTERNAL": "Iná platba",
    "pohoda.heading": "Pre účtovníctvo",
    "pohoda.lead": "Tržby ako súbor pre Pohodu: za každý deň a spôsob platby jeden príjmový pokladničný doklad, rozdelený podľa sadzieb DPH.",
    "pohoda.month": "Mesiac",
    "pohoda.toYesterday": "do včera",
    "pohoda.download": "Stiahnuť pre Pohodu",
    "pohoda.downloaded": "Stiahnuté. Súbor naimportujte v Pohode cez XML import.",
    "pohoda.noCashAccount": "V Pohode chýba pokladnica pre:",
    "pohoda.fixInSettings": "Doplňte ju v Nastaveniach.",
    "pohoda.noRegistrationId": "Firma nemá IČO. Doplňte ho v Nastaveniach.",
    "pohoda.nothing": "V tomto období sa nič nepredalo.",
    "pohoda.dayNotClosed": "Dnešný deň sa ešte neskončil.",
    "pohoda.notSetUp": "Nastavenie pre Pohodu je neplatné. Skontrolujte ho v Nastaveniach.",
    "pohoda.training": "Toto je školenie: nič odtiaľto sa neúčtuje, takže pre Pohodu nie je čo exportovať.",
    "pohoda.settings": "Pohoda",
    "pohoda.settingsLead": "Kam sa v Pohode účtuje každý spôsob platby. Skratky pokladníc a predkontácií opíšte presne tak, ako ich máte v Pohode — najlepšie podľa účtovníctva.",
    "pohoda.cashAccount": "Pokladnica",
    "pohoda.accounting": "Predkontácia",
    "pohoda.optional": "nepovinné",
    "pohoda.saved": "Uložené.",
    "pohoda.tooLong": "Najviac 19 znakov.",
    "devices.confirmUnpair": "Odpárovať „{name}“? Prestane fungovať pri ďalšom spojení so serverom.",
    "settings.heading": "Nastavenia",
    "settings.venue": "Prevádzka",
    "settings.timezone": "Časové pásmo",
    "settings.dayStart": "Obchodný deň začína",
    "settings.fiscalId": "Daňová registrácia",
    "settings.devices": "Pokladne",
    "settings.language": "Jazyk",

    "preview.badge": "Pripravujeme",
    "preview.note":
      "Toto je náčrt obrazovky, ktorá zatiaľ nie je hotová. Nič z toho nie sú skutočné dáta.",
    "preview.stock": "Sklad",
    "preview.purchasing": "Faktúry od dodávateľov",
    "preview.rota": "Rozpisy zmien",
    "preview.analytics": "Analytika",
    "preview.loyalty": "Vernosť",
    "modules.title": "Moduly",
    "modules.lead": "Čo tento podnik používa. Predaj, účtenky, pokladňa a EET sú vždy zapnuté; ostatné zapnete v rámci svojho tarifu.",
    "modules.plan": "Tarif: {plan}",
    "modules.noPlan": "Bez tarifu — len predaj a účtenky.",
    "modules.planLapsed": "Tarif vypršal, voliteľné moduly sú vypnuté. Predaj beží ďalej.",
    "modules.preset": "Typ podniku",
    "modules.custom": "Vlastný výber",
    "modules.save": "Uložiť moduly",
    "modules.saved": "Uložené. Pokladne a obrazovky to uvidia o pár sekúnd.",
    "modules.notInPlan": "Nie je v tarife",
    "modules.needs": "Potrebuje: {modules}",
    "modules.always": "Vždy zapnuté",
    "modules.soon": "Pripravujeme",
    "modules.noServiceMode": "Nechajte zapnuté stoly, pult alebo oboje — inak sa nedá predávať.",
    "preset.cafe": "Kaviareň",
    "preset.restaurant": "Reštaurácia",
    "preset.cafe-restaurant": "Kaviareň + reštaurácia",
    "preset.pub": "Krčma / bar",
    "preset.bakery": "Pekáreň / take-away",
    "preset.food-truck": "Pojazdné občerstvenie",
    "module.sales.name": "Predaj a účtenky",
    "module.sales.lead": "Pokladňa, platby, účtenky, zmeny, EET, menu, ľudia a tržby.",
    "module.tables.name": "Stoly",
    "module.tables.lead": "Plánik stolov, účty pri stole, presuny a rozdelenie.",
    "module.counter.name": "Pult",
    "module.counter.lead": "Predaj pri pulte: zaplatiť hneď, číslo objednávky, tu alebo so sebou.",
    "module.kitchen-display.name": "Kuchynská obrazovka",
    "module.kitchen-display.lead": "Lístky na obrazovke v kuchyni a na bare, alergény, odbavenie.",
    "module.kitchen-printing.name": "Tlač do kuchyne",
    "module.kitchen-printing.lead": "Lístky na tlačiareň v kuchyni a na bare.",
    "module.courses.name": "Chody",
    "module.courses.lead": "Predjedlo hneď, hlavný chod až na zavolanie.",
    "module.guest-ordering.name": "Objednávky od stola",
    "module.guest-ordering.lead": "Hosť si objedná z telefónu cez QR kód na stole.",
    "module.public-menu.name": "Menu na webe",
    "module.public-menu.lead": "Verejné menu s fotkami a alergénmi, vo viacerých jazykoch.",
    "module.online-ordering.name": "Objednávky so sebou online",
    "module.online-ordering.lead": "Objednávka a platba vopred, vyzdvihnutie pri pulte v daný čas.",
    "module.reservations.name": "Rezervácie",
    "module.reservations.lead": "Rezervácie stolov z webu aj telefonicky, na plániku stolov.",
    "module.stock.name": "Sklad",
    "module.stock.lead": "Suroviny, receptúry, odpis predajom, príjemky, inventúry, food cost.",
    "module.accounting.name": "Účtovníctvo",
    "module.accounting.lead": "Tržby ako súbor pre Pohodu.",
    "module.loyalty.name": "Vernostný program",
    "module.loyalty.lead": "Body a odmeny pre stálych hostí.",
    "module.crm.name": "Hostia",
    "module.crm.lead": "Kto k vám chodí a ako často.",
    "module.foodsafety.name": "Bezpečnosť potravín",
    "module.foodsafety.lead": "Záznamy HACCP a kontroly.",

    // --- Stock (the Sklad screens) ---------------------------------------------
    "nav.reservations": "Rezervácie",
    "res.title": "Rezervácie",
    "res.lead": "Kto dnes príde, koľko miest je kedy obsadených a rezervácie po telefóne.",
    "res.prev": "Predchádzajúci deň",
    "res.next": "Ďalší deň",
    "res.today": "Dnes",
    "res.covers": "Hostí",
    "res.bookings": "Rezervácií",
    "res.capacity": "Miest naraz",
    "res.strip": "Obsadenosť počas večera",
    "res.none": "Na tento deň nikto nemá rezerváciu.",
    "res.time": "Čas",
    "res.name": "Meno",
    "res.party": "Osôb",
    "res.phone": "Telefón",
    "res.table": "Stôl",
    "res.note": "Poznámka",
    "res.source": "Odkiaľ",
    "res.status": "Stav",
    "res.source.ONLINE": "web",
    "res.source.PHONE": "telefón",
    "res.source.WALK_IN": "bez rezervácie",
    "res.status.BOOKED": "príde",
    "res.status.SEATED": "usadení",
    "res.status.NO_SHOW": "neprišli",
    "res.status.CANCELLED": "zrušené",
    "res.seat": "Usadiť",
    "res.noShow": "Neprišli",
    "res.cancel": "Zrušiť",
    "res.restore": "Vrátiť",
    "res.noTable": "— bez stola —",
    "res.add": "Nová rezervácia",
    "res.addHint": "Po telefóne alebo pre hosťa, ktorý sa zastavil.",
    "res.save": "Zapísať",
    "res.full": "V tom čase je plno. Zapísať aj tak?",
    "res.force": "Zapísať aj tak",
    "res.closed": "V tom čase máte zatvorené.",
    "res.saved": "Zapísané.",
    "nav.history": "História zmien",
    "nav.printers": "Tlačiarne",
    "print.lead": "Čo ktorá tlačiareň dostala — aj to, čo sa nevytlačilo, a prečo.",
    "print.none": "Zatiaľ žiadna tlačiareň. Kuchyňa má obrazovky a bloky sa ukazujú na pokladni.",
    "print.empty": "Za posledných 24 hodín nič.",
    "print.unrouted": "Bez tlačiarne",
    "print.transport.SCREEN": "Na obrazovke",
    "print.transport.HUB": "Tlačiareň v sieti podniku",
    "print.transport.CLOUDPRNT": "Tlačiareň Star cez CloudPRNT",
    "print.columns": "{n} znakov na riadok",
    "print.receipts": "bloky z pokladne",
    "print.status.PRINTED": "Vytlačené {at}",
    "print.status.QUEUED": "Čaká na tlačiareň",
    "print.status.STALE": "Nevytlačené: odoslané o {fired}, k tlačiarni prišlo až o {at}",
    "print.status.UNROUTED": "Nevytlačené: pre {station} netlačí žiadna tlačiareň",
    "print.copy": "Kópia",
    "print.reprint": "Vytlačiť znova",
    "print.reprinted": "Kópia odoslaná na tlačiareň.",
    "print.setup": "Nastaviť tlačiareň",
    "print.form.name": "Názov",
    "print.form.transport": "Pripojenie",
    "print.form.columns": "Šírka kotúča",
    "print.form.stations": "Tlačí lístky pre",
    "print.form.receipts": "Tlačí bloky z pokladne",
    "print.form.active": "Zapnutá",
    "print.form.save": "Uložiť tlačiareň",
    "print.saved": "Uložené — pokladne to vedia.",
    "print.forbidden": "Tlačiarne vidí vedúci zmeny a vyššie.",
    "audit.printer.saved": "Tlačiareň {subject} nastavená",
    "audit.print.reprinted": "Kópia na tlačiarni {subject}",
    "audit.n.lines.one": "{n} položka",
    "audit.n.lines.few": "{n} položky",
    "audit.n.lines.many": "{n} položky",
    "audit.n.lines.other": "{n} položiek",
    "audit.n.seats.one": "{n} miesto",
    "audit.n.seats.few": "{n} miesta",
    "audit.n.seats.many": "{n} miesta",
    "audit.n.seats.other": "{n} miest",
    "audit.n.changes.one": "{n} zmena",
    "audit.n.changes.few": "{n} zmeny",
    "audit.n.changes.many": "{n} zmeny",
    "audit.n.changes.other": "{n} zmien",
    "audit.title": "História zmien",
    "audit.lead": "Kto čo v kancelárii zmenil — ceny, ľudia, zariadenia, sklad. Záznam sa nedá upraviť bez toho, aby to bolo vidieť.",
    "audit.intact": "Záznam je neporušený — overené: {n}.",
    "audit.broken": "Záznam bol zmenený: pri zmene č. {n} nesedí odtlačok. Ozvite sa nám.",
    "audit.none": "V tomto období sa nič nemenilo.",
    "audit.when": "Kedy",
    "audit.who": "Kto",
    "audit.what": "Čo",
    "audit.system": "Systém",
    "audit.forbidden": "Históriu zmien vidí vedúci a majiteľ.",
    "audit.menu.item.created": "Nové jedlo {subject} za {price}",
    "audit.menu.item.changed": "Úprava jedla {subject}",
    "audit.menu.item.priced": "Cena jedla {subject}: {before} → {after}",
    "audit.menu.published": "Lístok odoslaný pokladniciam (verzia {version})",
    "audit.staff.added": "Nový člen tímu: {subject}",
    "audit.staff.changed": "Zmena pri {subject}",
    "audit.staff.left": "{subject} už u nás nepracuje",
    "audit.staff.pin.reset": "Nový PIN pre {subject}",
    "audit.device.pairing.issued": "Kód na spárovanie nového zariadenia",
    "audit.device.unpaired": "Zariadenie odpárované",
    "audit.device.service.changed": "Zariadenie prepnuté: {mode}",
    "audit.mode.COUNTER": "pult {prefix}",
    "audit.mode.TABLE": "k stolom",
    "audit.venue.identity.changed": "Údaje o firme upravené",
    "audit.venue.table.saved": "Stôl {subject}: {seats}",
    "audit.venue.published": "Nastavenia odoslané pokladniciam (verzia {version})",
    "audit.venue.modules.changed": "Moduly zmenené",
    "audit.venue.public.changed": "Verejná stránka upravená",
    "audit.stock.received": "Príjem tovaru: {lines} za {price}",
    "audit.stock.wasted": "Odpis: {lines}",
    "audit.stock.counted": "Inventúra: {lines}",
    "audit.stock.item.saved": "Surovina {subject} upravená",
    "audit.stock.supplier.saved": "Dodávateľ {subject}",
    "audit.stock.recipe.versioned": "Receptúra {subject}, verzia {version}",
    "audit.stock.option-delta.versioned": "Čo mení voľba hosťa, verzia {version}",
    "audit.booking.made": "Rezervácia pre {party}",
    "audit.booking.forced": "Rezervácia pre {party} nad kapacitu",
    "audit.booking.changed": "Rezervácia: {status}",
    "nav.stock": "Sklad",
    "stock.tab.overview": "Prehľad",
    "stock.tab.items": "Suroviny",
    "stock.tab.recipes": "Receptúry",
    "stock.tab.receive": "Príjem",
    "stock.tab.count": "Inventúra",
    "stock.tab.waste": "Odpisy",
    "stock.tab.suppliers": "Dodávatelia",
    "stock.value": "Hodnota skladu",
    "stock.valueHint": "V priemerných nákupných cenách bez DPH.",
    "stock.foodCost": "Food cost · podiel surovín",
    "stock.foodCostHint": "Suroviny predaných jedál ako podiel ich tržby bez DPH — len jedlá s receptúrou.",
    "stock.period.today": "Dnes",
    "stock.period.week": "7 dní",
    "stock.period.month": "30 dní",
    "stock.running": "Dochádza",
    "stock.runningNone": "Nič nedochádza.",
    "stock.runningLine": "zostáva {left}, objednať pod {min}",
    "stock.byDish": "Podľa jedál",
    "stock.dish": "Jedlo",
    "stock.sold": "Predané",
    "stock.takings": "Tržba bez DPH",
    "stock.cost": "Suroviny za",
    "stock.share": "Podiel",
    "stock.noSales": "V tomto období sa nepredalo nič s receptúrou.",
    "stock.unmapped": "Predaje bez receptúry",
    "stock.unmappedHint": "Pri týchto položkách sklad nič neodpisuje. Doplňte im receptúru — platí od chvíle uloženia.",
    "stock.timesSold": "predané {n}×",
    "stock.writeRecipe": "Napísať receptúru",
    "stock.problems": "Čo sklad nedokázal zapísať",
    "stock.addItem": "Pridať surovinu",
    "stock.item.name": "Názov",
    "stock.item.category": "Skupina",
    "stock.item.categoryHint": "Napr. Bar, Kuchyňa, Pivnica — tak sa suroviny radia aj v inventúre.",
    "stock.item.onHand": "Na sklade",
    "stock.item.unitCost": "Cena",
    "stock.item.value": "Hodnota",
    "stock.item.unit": "Vedie sa v",
    "stock.item.unitHint": "Po prvom pohybe na sklade už jednotku nemožno zmeniť.",
    "stock.item.reorder": "Objednať, keď klesne pod",
    "stock.item.standardCost": "Cena, kým príde prvá dodávka",
    "stock.item.standardCostHint": "Kč bez DPH za {unit}. Potom sa počíta priemer z dodávok.",
    "stock.item.active": "Používame",
    "stock.item.low": "dochádza",
    "stock.unit.g": "kilogramoch (kg)",
    "stock.unit.ml": "litroch (l)",
    "stock.unit.ea": "kusoch (ks)",
    "stock.noItems": "Zatiaľ žiadne suroviny. Pridajte prvú — napríklad kávu alebo mlieko.",
    "stock.perUnit": "za {unit}",
    "stock.saved": "Uložené.",
    "stock.recipe.lead": "Čo spotrebuje jedna porcia. Nová verzia platí od uloženia; čo sa predalo skôr, zostáva podľa starej.",
    "stock.recipe.portionCost": "Porcia stojí",
    "stock.recipe.price": "Cena bez DPH",
    "stock.recipe.none": "bez receptúry",
    "stock.recipe.uncosted": "Zatiaľ bez ceny: {items}",
    "stock.recipe.ingredient": "Surovina",
    "stock.recipe.quantity": "Množstvo",
    "stock.recipe.save": "Uložiť novú verziu",
    "stock.recipe.version": "verzia {n}",
    "stock.recipe.saved": "Uložené. Platí pre všetko, čo sa predá odteraz.",
    "stock.options": "Voľby hostí",
    "stock.options.lead": "Čo voľba zmení v sklade, na jeden kus: ovsené mlieko je +0,2 l ovseného a −0,2 l mlieka.",
    "stock.options.none": "v sklade nič nemení",
    "stock.options.signedHint": "Kladné množstvo pridá, záporné uberie.",
    "stock.receive.supplier": "Dodávateľ",
    "stock.receive.noSupplier": "— bez dodávateľa —",
    "stock.receive.document": "Číslo dodacieho listu alebo faktúry",
    "stock.receive.netTotal": "Spolu bez DPH (Kč)",
    "stock.receive.submit": "Prijať na sklad",
    "stock.receive.done": "Prijaté. Priemerné ceny sú prepočítané.",
    "stock.recent": "Posledné pohyby",
    "stock.line.add": "Ďalší riadok",
    "stock.line.remove": "Odobrať riadok",
    "stock.line.pick": "— vyberte surovinu —",
    "stock.line.incomplete": "Doplňte pri každom riadku surovinu a množstvo.",
    "stock.pending": "Zapísané; sklad sa prepočíta v priebehu pár sekúnd.",
    "stock.count.last": "Posledná inventúra · {when}",
    "stock.count.lastExact": "Všetko sedelo.",
    "stock.count.lead": "Spočítajte, čo je na regáli, bez ohľadu na to, čo hovorí systém. Rozdiely uvidíte skôr, než sa čokoľvek zapíše.",
    "stock.count.counted": "Napočítané",
    "stock.count.expected": "Podľa systému",
    "stock.count.variance": "Rozdiel",
    "stock.count.check": "Skontrolovať rozdiely",
    "stock.count.post": "Zapísať inventúru",
    "stock.count.back": "Upraviť",
    "stock.count.done": "Inventúra zapísaná. Sklad zodpovedá napočítanému.",
    "stock.count.total": "Rozdiel spolu",
    "stock.count.empty": "Napíšte aspoň jedno množstvo.",
    "stock.waste.reason": "Dôvod",
    "stock.waste.submit": "Odpísať",
    "stock.waste.done": "Odpísané.",
    "stock.reason.SPOILED": "Pokazené",
    "stock.reason.DAMAGED": "Poškodené",
    "stock.reason.EXPIRED": "Po záruke",
    "stock.reason.PREP_ERROR": "Chyba v kuchyni",
    "stock.reason.RETURNED_BY_GUEST": "Vrátené hosťom",
    "stock.reason.STAFF_MEAL": "Jedlo pre personál",
    "stock.reason.VOID_AFTER_FIRE": "Storno po odoslaní do kuchyne",
    "stock.reason.COUNT": "Inventúra",
    "stock.reason.LATE_BEFORE_COUNT": "Predaj z času pred inventúrou",
    "stock.kind.RECEIPT": "Príjem",
    "stock.kind.SALE_DEPLETION": "Predaj",
    "stock.kind.WASTE": "Odpis",
    "stock.kind.STAFF_MEAL": "Personál",
    "stock.kind.COUNT_ADJUSTMENT": "Inventúra",
    "stock.supplier.add": "Pridať dodávateľa",
    "stock.supplier.registration": "IČO",
    "stock.supplier.none": "Zatiaľ žiadni dodávatelia.",
    "today.lowStock": "Dochádza: {items}",
    "stock.options.edit": "Upraviť",
    "stock.unmappedMore": "a ďalších {n}",
    "approvals.title": "Schválené bez oprávnenia",
    "approvals.lead": "Pokladnica to mala odmietnuť. Predaj platí, len by to mal niekto preveriť.",
    "approvals.act.VOID_SENT": "Storno jedla, ktoré už mala kuchyňa",
    "approvals.act.COMP": "Zadarmo",
    "approvals.act.DISCOUNT": "Zľava",
    "approvals.act.REFUND": "Vrátenie peňazí",
    "approvals.problem.NOT_PERMITTED": "{name} na to nemá oprávnenie",
    "approvals.problem.UNKNOWN_APPROVER": "schválila neznáma osoba ({name})",
    "approvals.problem.INACTIVE_APPROVER": "{name} už u vás nepracuje",
    "approvals.open": "Otvoriť účet",
    "checks.pickup": "Výdaj {code}",
    "tender.ONLINE": "Online platba",
    "public.title": "Verejná stránka a objednávky",
    "public.lead": "Kde vás hostia nájdu: jedálny lístok, otváracie hodiny a objednávky s vyzdvihnutím.",
    "public.slug": "Adresa stránky",
    "public.slugHint": "Malé písmená, číslice a pomlčky — napríklad „lipa-vinohrady“.",
    "public.notYet": "Stránka zatiaľ nemá adresu.",
    "public.open": "Otvoriť stránku",
    "public.tagline": "Krátky popis",
    "public.address": "Adresa",
    "public.phone": "Telefón",
    "public.hours": "Otváracie hodiny",
    "public.hoursHint": "Zatvárací čas pred otváracím znamená po polnoci.",
    "public.closed": "zatvorené",
    "public.addRange": "+ ďalší čas",
    "public.day.mon": "Pondelok",
    "public.day.tue": "Utorok",
    "public.day.wed": "Streda",
    "public.day.thu": "Štvrtok",
    "public.day.fri": "Piatok",
    "public.day.sat": "Sobota",
    "public.day.sun": "Nedeľa",
    "public.online": "Prijímame online objednávky",
    "public.onlineHint": "Vypnutím objednávky hneď pozastavíte — napríklad keď je plno.",
    "public.prep": "Príprava (minúty)",
    "public.slot": "Časy vyzdvihnutia po (minútach)",
    "public.bookings": "Prijímame rezervácie online",
    "public.bookingsHint": "Hostia si stôl zarezervujú na vašej stránke. Telefonicky môžete rezervovať vždy.",
    "public.capacity": "Hostí naraz",
    "public.duration": "Stôl držíme (minúty)",
    "public.bookingSlot": "Časy rezervácií po (minútach)",
    "public.maxParty": "Najväčšia skupina online",
    "public.daysAhead": "Koľko dní dopredu",
    "public.notice": "Najneskôr vopred (minúty)",
    "public.saved": "Uložené. Stránka sa zmení hneď.",
    "menu.guests": "Pre hostí online",
    "menu.photo": "Fotka",
    "menu.photoUpload": "Nahrať fotku",
    "menu.photoRemove": "Odobrať fotku",
    "menu.photoHint": "Zmenšíme ju v prehliadači — na server ide len malá kópia.",
    "menu.photoFailed": "Fotku sa nepodarilo nahrať.",
    "menu.nameEn": "Názov po anglicky",
    "menu.descriptionEn": "Popis po anglicky",
    "menu.dietary": "Vhodné pre",
    "menu.diet.VEGAN": "vegánov",
    "menu.diet.VEGETARIAN": "vegetariánov",
    "menu.diet.GLUTEN_FREE": "bez lepku",
    "menu.diet.LACTOSE_FREE": "bez laktózy",
    "menu.diet.SPICY": "pikantné",
    "menu.featured": "Odporúčame — navrchu stránky",
  },
};
